import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MomLayout from './MomLayout';
import { useMomAuth } from '../../context/MomAuthContext';
import { generationApi } from '../../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Sparkles, Loader2, X, Download,
  Camera, RefreshCw, AlertCircle, Video
} from 'lucide-react';
import { InsufficientCreditsModal } from '../../components/credits/InsufficientCreditsModal';

const MIN_WEEK = 18;

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => resolve(e.target.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const stripPrefix = (dataUrl) => (dataUrl?.includes('base64,') ? dataUrl.split('base64,')[1] : dataUrl);

export default function MomUploadPage() {
  const navigate = useNavigate();
  const { mom } = useMomAuth();
  const pollRef = useRef(null);

  const [ultrasoundDataUrl, setUltrasoundDataUrl] = useState(null);
  const [gender, setGender] = useState(
    mom?.baby_gender && mom.baby_gender !== 'unknown' ? mom.baby_gender : 'female'
  );
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState('');
  const [resultImage, setResultImage] = useState(null);
  const [creditsError, setCreditsError] = useState(null);
  const [weekError, setWeekError] = useState(null);

  const week = mom?.pregnancy_week || 0;
  const blockedByWeek = week > 0 && week < MIN_WEEK;

  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Görüntü 10MB\'den küçük olmalı');
      return;
    }
    const dataUrl = await fileToBase64(file);
    setUltrasoundDataUrl(dataUrl);
  };

  const startPolling = (jobId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await generationApi.getScientificUltrasound4KStatus(jobId);
        if (res?.progress) setProgress(res.progress);
        if (res?.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (res?.enhanced_image) {
            setResultImage(res.enhanced_image.startsWith('data:')
              ? res.enhanced_image
              : `data:image/png;base64,${res.enhanced_image}`);
            toast.success('4K bebek yüzü hazır!');
          } else {
            toast.error('Görsel oluşturulamadı, lütfen tekrar deneyin.');
          }
          setGenerating(false);
        } else if (res?.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.error(res?.error || 'Üretim başarısız oldu');
          setGenerating(false);
        }
      } catch (err) {
        // transient — keep polling
        console.warn('Status poll error', err);
      }
    }, 3000);
  };

  const onGenerate = useCallback(async () => {
    if (!ultrasoundDataUrl) {
      toast.error('Lütfen önce ultrason görüntüsünü yükleyin.');
      return;
    }
    if (blockedByWeek) {
      setWeekError(`4K bebek yüzü için en az ${MIN_WEEK}. haftada olmalısın. Şu an ${week}. haftadasın.`);
      return;
    }
    setGenerating(true);
    setResultImage(null);
    setProgress('Üretim başlatılıyor...');
    setCreditsError(null);
    setWeekError(null);

    try {
      const startRes = await generationApi.startScientificUltrasound4K({
        image: stripPrefix(ultrasoundDataUrl),
        patient_id: mom?.id || 'mom',
        scan_id: null,
        side_image: stripPrefix(ultrasoundDataUrl),
        front_image: stripPrefix(ultrasoundDataUrl),
        biometrics: {
          bpd: 0, hc: 0, ac: 0, fl: 0, ofd: 0,
          nasal_bone: 0, iod: 0, fromen: 0, eye_diameter: 0,
          bioc_cap: 0, mouth_diameter: 0, upper_lip: 0, chin: 0,
          onarka_bas: 0,
        },
        pregnancy_week: parseInt(week) || 24,
        gender,
        view_type: 'original',
      });
      if (startRes?.job_id) {
        toast.success('Üretim başlatıldı! AI işliyor...');
        startPolling(startRes.job_id);
      } else {
        setGenerating(false);
        toast.error('Üretim başlatılamadı.');
      }
    } catch (err) {
      setGenerating(false);
      if (err?.status === 402 && err?.detail) {
        setCreditsError(err.detail);
      } else if (err?.status === 422 && err?.detail?.code === 'PREGNANCY_WEEK_TOO_EARLY') {
        setWeekError(err.detail.message_tr);
      } else {
        toast.error(err?.message || 'Bir hata oluştu');
      }
    }
  }, [ultrasoundDataUrl, gender, mom, week, blockedByWeek]);

  const onDownload = () => {
    if (!resultImage) return;
    const a = document.createElement('a');
    a.href = resultImage;
    a.download = `bebek_4k_${(mom?.first_name || 'mom')}_${new Date().toISOString().split('T')[0]}.png`;
    a.click();
  };

  const onReset = () => {
    setResultImage(null);
    setUltrasoundDataUrl(null);
    setProgress('');
  };

  const goToVideo = () => {
    if (!resultImage) return;
    try {
      sessionStorage.setItem('mom_video_seed', resultImage);
    } catch {/* ignore */}
    navigate('/mom/video');
  };

  const canGenerate = !!ultrasoundDataUrl && !generating && !blockedByWeek;

  return (
    <MomLayout>
      {/* Top bar */}
      <div className="flex items-center justify-between mb-5">
        <button
          onClick={() => navigate('/mom/dashboard')}
          className="w-10 h-10 rounded-2xl bg-white border border-[#F3E5E0] flex items-center justify-center text-[#7A6C68] hover:text-[#FB7185]"
          data-testid="mom-upload-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="text-center flex-1 px-3">
          <p className="text-xs text-[#7A6C68]">Ultrason · 4K</p>
          <h1 className="text-base font-bold text-[#2D2422]">4K Bebek Yüzü</h1>
        </div>
        <div className="w-10" />
      </div>

      {!resultImage ? (
        <>
          {/* Hero */}
          <div
            className="rounded-3xl p-5 mb-5 shadow-sm relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, #FB7185, #F472B6)' }}
            data-testid="mom-upload-hero"
          >
            <Sparkles className="absolute top-4 right-4 w-5 h-5 text-white/40" />
            <p className="text-white/85 text-xs font-medium mb-1">Sadece ultrasonun yeterli</p>
            <h2 className="text-xl font-bold text-white leading-snug mb-2">
              Bir ultrason fotoğrafından <br /> 4K bebek yüzünü oluştur
            </h2>
            <p className="text-white/90 text-xs leading-relaxed">
              Ölçüm girmene gerek yok. AI ultrasondan yüzü çıkarıp 4K netliğinde sunar.
              Her üretim <span className="font-semibold">1 kredi</span> tüketir.
            </p>
          </div>

          {/* Pregnancy week warning */}
          {blockedByWeek && (
            <div className="rounded-2xl bg-[#FEF2F2] border border-[#FCA5A5] p-4 mb-4 flex items-start gap-3"
              data-testid="mom-upload-week-warning">
              <AlertCircle className="w-5 h-5 text-[#DC2626] flex-none mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-[#991B1B] mb-1">Henüz çok erken</p>
                <p className="text-xs text-[#7F1D1D] leading-relaxed">
                  4K bebek yüzü için <strong>en az {MIN_WEEK}. haftada</strong> olmalısın. Şu an <strong>{week}. haftadasın</strong>.
                  Genetik Füzyon (anne + baba fotoğrafından bebek yüzü) seçeneğini deneyebilirsin.
                </p>
                <button
                  onClick={() => navigate('/mom/ai-face')}
                  className="text-xs text-[#FB7185] font-semibold mt-2 underline"
                  data-testid="mom-upload-fallback-aiface-btn"
                >
                  Genetik Füzyon'a git →
                </button>
              </div>
            </div>
          )}

          {/* Ultrasound upload */}
          <p className="text-sm font-semibold text-[#2D2422] mb-2">
            Ultrason fotoğrafı <span className="text-[#FB7185]">*</span>
          </p>
          <div
            className={`relative aspect-[4/5] rounded-3xl border-2 border-dashed flex items-center justify-center overflow-hidden transition-all mb-5 ${
              ultrasoundDataUrl ? 'border-transparent bg-white shadow-sm' : 'border-[#F3D8D0] bg-[#FFF6F3]'
            }`}
            data-testid="mom-upload-ultrasound-tile"
          >
            {ultrasoundDataUrl ? (
              <>
                <img src={ultrasoundDataUrl} alt="Ultrason" className="w-full h-full object-cover" />
                <button
                  onClick={() => setUltrasoundDataUrl(null)}
                  className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                  data-testid="mom-upload-ultrasound-remove"
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <label className="cursor-pointer w-full h-full flex flex-col items-center justify-center text-center px-6 gap-3">
                <div className="w-16 h-16 rounded-2xl bg-[#FFE4DF] text-[#FB7185] flex items-center justify-center">
                  <Upload className="w-7 h-7" />
                </div>
                <p className="text-sm font-medium text-[#2D2422]">Ultrasonu yükle</p>
                <p className="text-xs text-[#7A6C68] leading-relaxed max-w-[220px]">
                  Profil veya frontal görünüm tercih edilir. Net, parlak bir kare seçmen yeter.
                </p>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFile}
                  data-testid="mom-upload-ultrasound-input"
                />
              </label>
            )}
          </div>

          {/* Gender */}
          <p className="text-sm font-semibold text-[#2D2422] mb-2">Cinsiyet</p>
          <div className="grid grid-cols-2 gap-3 mb-5">
            <button
              type="button"
              onClick={() => setGender('female')}
              className={`rounded-2xl p-3 border text-sm font-medium transition-all ${
                gender === 'female'
                  ? 'bg-[#FB7185] border-[#FB7185] text-white shadow-sm'
                  : 'bg-white border-[#F3E5E0] text-[#7A6C68]'
              }`}
              data-testid="mom-upload-gender-female"
            >
              Kız
            </button>
            <button
              type="button"
              onClick={() => setGender('male')}
              className={`rounded-2xl p-3 border text-sm font-medium transition-all ${
                gender === 'male'
                  ? 'bg-[#3B82F6] border-[#3B82F6] text-white shadow-sm'
                  : 'bg-white border-[#F3E5E0] text-[#7A6C68]'
              }`}
              data-testid="mom-upload-gender-male"
            >
              Erkek
            </button>
          </div>

          {/* Inline week error from server */}
          {weekError && !blockedByWeek && (
            <div className="rounded-2xl bg-[#FEF2F2] border border-[#FCA5A5] p-3 mb-4 flex items-start gap-2"
              data-testid="mom-upload-server-week-error">
              <AlertCircle className="w-4 h-4 text-[#DC2626] flex-none mt-0.5" />
              <p className="text-xs text-[#7F1D1D]">{weekError}</p>
            </div>
          )}

          {/* Generate button */}
          <button
            onClick={onGenerate}
            disabled={!canGenerate}
            className={`w-full h-14 rounded-2xl text-white font-semibold shadow-sm transition-all flex items-center justify-center gap-2 ${
              canGenerate
                ? 'bg-gradient-to-r from-[#FB7185] to-[#F472B6] hover:shadow-md active:scale-[0.98]'
                : 'bg-[#E5DAD6] cursor-not-allowed'
            }`}
            data-testid="mom-upload-generate-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {progress || 'Üretiliyor...'}
              </>
            ) : (
              <>
                <Camera className="w-5 h-5" />
                4K Bebek Yüzü Oluştur · 1 kredi
              </>
            )}
          </button>

          {!ultrasoundDataUrl && !blockedByWeek && (
            <p className="text-xs text-[#7A6C68] mt-3 text-center" data-testid="mom-upload-validation-hint">
              Ultrason yüklemen yeterli — gerisini AI halleder.
            </p>
          )}
        </>
      ) : (
        /* Result view */
        <div className="space-y-4" data-testid="mom-upload-result">
          <div className="rounded-3xl bg-white p-5 shadow-sm border border-[#F3E5E0]">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-[#FB7185]/30 to-[#F472B6]/30 rounded-3xl blur-xl" />
              <img
                src={resultImage}
                alt="4K bebek yüzü"
                className="relative w-full aspect-[4/5] object-cover rounded-2xl"
                data-testid="mom-upload-result-image"
              />
            </div>
            <p className="text-center text-xs text-[#7A6C68] mt-3">
              Bu görsel AI yorumudur — gerçek bir tıbbi tanı belgesi değildir.
            </p>
          </div>

          <button
            onClick={goToVideo}
            className="w-full h-12 rounded-2xl bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98]"
            data-testid="mom-upload-go-video-btn"
          >
            <Video className="w-4 h-4" />
            Bu yüzü canlandır · 2 kredi
          </button>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={onDownload}
              className="rounded-2xl bg-white border border-[#F3E5E0] py-3 text-sm font-medium text-[#2D2422] flex items-center justify-center gap-2 active:scale-[0.98]"
              data-testid="mom-upload-download-btn"
            >
              <Download className="w-4 h-4" />
              İndir
            </button>
            <button
              onClick={onReset}
              className="rounded-2xl bg-[#FFE4DF] py-3 text-sm font-medium text-[#FB7185] flex items-center justify-center gap-2 active:scale-[0.98]"
              data-testid="mom-upload-reset-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Yenisini Üret
            </button>
          </div>
        </div>
      )}

      <InsufficientCreditsModal
        open={!!creditsError}
        onClose={() => setCreditsError(null)}
        detail={creditsError}
      />
    </MomLayout>
  );
}
